import React from 'react';
import { Box, Divider, Paper, styled, Typography } from '@mui/material';
import { ReadOnlyBlock } from '@blumtechgroup/blum-react-core-components';
import { gql, useQuery } from '@apollo/client';

interface Option {
  id: string;
  text: string;
  responseMessage?: string;
  nextQuestions?: string[];
  isYes?: boolean;
}

interface NestedQuestion {
  id: string;
  text: string;
  yesInfoMessage?: string;
  noInfoMessage?: string;
  yesFollowUps?: NestedQuestion[];
  noFollowUps?: NestedQuestion[];
}

interface Question {
  id: string;
  text: string;
  options?: Option[];
  yesFollowUps?: NestedQuestion[];
  noFollowUps?: NestedQuestion[];
  yesInfoMessage?: string;
  noInfoMessage?: string;
}

interface Questionnaire {
  id: string;
  name: string;
  questions: Question[];
}


const QuestionnaireContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
}));

const QuestionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  marginBottom: theme.spacing(2),
}));


const YesContainer = styled(Box)(({ theme }) => ({
  borderLeft: `2px solid ${theme.palette.success.light}`,
  paddingLeft: theme.spacing(2),
  marginLeft: theme.spacing(4),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const NoContainer = styled(Box)(({ theme }) => ({
  borderLeft: `2px solid ${theme.palette.error.light}`,
  paddingLeft: theme.spacing(2),
  marginLeft: theme.spacing(4),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const InfoMessage = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.9rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
}));


const GET_QUESTIONNAIRE = gql`
  query GetQuestionnaire($id: uuid!) {
    dans_fund_questionnaire_by_pk(id: $id) {
      id
      name
      questions
    }
  }
`;

interface Props {
  questionnaireId?: string;
  handleEdit?: () => void;
}

const ViewDansFundQuestionnaire = ({
                                     questionnaireId,
                                     handleEdit,
                                   }: Props): React.ReactElement => {
  const { data, loading, error } = useQuery(GET_QUESTIONNAIRE, {
    variables: { id: questionnaireId || '' },
    skip: !questionnaireId,
    fetchPolicy: 'no-cache',
  });

  const { data: allQuestionnairesData } = useQuery(
    gql`
      query GetAllQuestionnaires {
        dans_fund_questionnaire(limit: 1) {
          id
          name
          questions
        }
      }
    `,
    { fetchPolicy: 'no-cache', skip: !!questionnaireId },
  );

  let rawQuestionnaire = data?.dans_fund_questionnaire_by_pk ||
    (allQuestionnairesData?.dans_fund_questionnaire?.[0] || null);

  let questionnaire: Questionnaire | null = null;
  if (rawQuestionnaire) {
    try {
      const parsedQuestions = typeof rawQuestionnaire.questions === 'string'
        ? JSON.parse(rawQuestionnaire.questions)
        : rawQuestionnaire.questions;

      questionnaire = {
        ...rawQuestionnaire,
        questions: Array.isArray(parsedQuestions) ? parsedQuestions : [],
      };
    } catch (e) {
      console.error('Error parsing questionnaire data:', e);
      questionnaire = {
        ...rawQuestionnaire,
        questions: [],
      };
    }
  }

  if (loading) {
    return <Typography>Loading questionnaire...</Typography>;
  }

  if (error || (!questionnaire && !loading)) {
    return <Typography color="error">Error loading questionnaire</Typography>;
  }

  if (!questionnaire) {
    return (
      <ReadOnlyBlock
        items={[
          {
            label: 'Dan\'s Fund Questionnaire',
            value: 'No questionnaire available',
          },
        ]}
        handleEdit={handleEdit}
      />
    );
  }


  if (handleEdit) {
    const questionsWithStructure = questionnaire?.questions?.map((question, index) => {
      return {
        label: `Question ${index + 1}`,
        value: question.text,
        nestedItems: question?.options?.map(option => {
          const linkedQuestions = option.nextQuestions?.map(nextQuestionId => {
            const nextQuestion = questionnaire?.questions.find(q => q.id === nextQuestionId);
            return nextQuestion ? nextQuestion.text : 'Unknown Question';
          }).join(', ') || 'No linked questions';

          return {
            label: option.isYes ? 'Yes' : 'No',
            value: `${option.text}${option.responseMessage ? ` - Response: ${option.responseMessage}` : ''}${linkedQuestions !== 'No linked questions' ? ` - Links to: ${linkedQuestions}` : ''}`,
          };
        }),
      };
    });

    return (
      <ReadOnlyBlock
        items={[
          { label: 'Questionnaire Name', value: questionnaire.name },
          ...questionsWithStructure,
        ]}
        handleEdit={handleEdit}
      />
    );
  }

  const renderNestedQuestion = (question: NestedQuestion | undefined, prefix: string, depth: number) => {
    if (!question) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          <Box component="span"
               sx={{ display: 'inline-block', width: 20 }}>-</Box>
          Nested Question {prefix}: "{question.text}"
        </Typography>

        <Box sx={{ pl: 4 }}>
          {/* Yes option */}
          <Typography variant="body2"
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span"
                 sx={{ display: 'inline-block', width: 20 }}>-</Box> Yes
          </Typography>

          {/* Yes info message and nested follow-ups */}
          <Box sx={{ pl: 4 }}>
            {question.yesInfoMessage && (
              <Typography variant="body2" color="text.secondary"
                          sx={{ mb: 1, fontStyle: 'italic' }}>
                Display informative message: "{question.yesInfoMessage}"
              </Typography>
            )}

            {/* Recursively render nested Yes follow-ups */}
            {question.yesFollowUps && question.yesFollowUps.map((followUp: NestedQuestion, index: number) =>
              renderNestedQuestion(followUp, `${prefix}.1.${index + 1}`, depth + 1),
            )}
          </Box>

          {/* No option */}
          <Typography variant="body2" sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 0.5,
            mt: 0.5,
          }}>
            <Box component="span"
                 sx={{ display: 'inline-block', width: 20 }}>-</Box> No
          </Typography>

          {/* No info message and nested follow-ups */}
          <Box sx={{ pl: 4 }}>
            {question.noInfoMessage && (
              <Typography variant="body2" color="text.secondary"
                          sx={{ mb: 1, fontStyle: 'italic' }}>
                Display informative message: "{question.noInfoMessage}"
              </Typography>
            )}

            {/* Recursively render nested No follow-ups */}
            {question.noFollowUps && question.noFollowUps.map((followUp: NestedQuestion, index: number) =>
              renderNestedQuestion(followUp, `${prefix}.2.${index + 1}`, depth + 1),
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <QuestionnaireContainer>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
          {questionnaire?.name}
        </Typography>
        <Divider sx={{ mb: 4 }} />

        {questionnaire?.questions?.map((question: Question, index: number) => (
          <Box key={question.id} sx={{ mb: 4 }}>
            {/* Main question with number */}
            <QuestionTitle variant="body1" gutterBottom>
              Main Question {index + 1}: "{question.text}"
            </QuestionTitle>

            <Box
              sx={{ display: 'flex', flexDirection: 'column', pl: 2, mt: 1 }}>
              {/* Yes option */}
              <Typography sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box component="span"
                     sx={{ display: 'inline-block', width: 20 }}>-</Box> Yes
              </Typography>

              {/* Yes info message and follow-up */}
              <YesContainer>
                {/* Handle both new and old data structure */}
                {question.yesInfoMessage && (
                  <InfoMessage>
                    {question.yesInfoMessage}
                  </InfoMessage>
                )}

                {question.options && question.options.length > 0 &&
                  question.options.find(opt => opt.isYes)?.responseMessage && (
                    <InfoMessage>
                      {question.options.find(opt => opt.isYes)?.responseMessage}
                    </InfoMessage>
                  )}

                {/* Render Yes follow-up questions */}
                {question.yesFollowUps && question.yesFollowUps.map((followUp: NestedQuestion, fIndex: number) =>
                  renderNestedQuestion(followUp, `${index + 1}a.${fIndex + 1}`, 1),
                )}

                {/* Render linked questions (old structure) */}
                {question.options && question.options.find(opt => opt.isYes)?.nextQuestions?.map((nextQuestionId, idx) => {
                  const nextQuestion = questionnaire?.questions.find(q => q.id === nextQuestionId);
                  return nextQuestion ? (
                    <Box key={`yes-next-${idx}`} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <Box component="span"
                             sx={{ display: 'inline-block', width: 20 }}>-</Box>
                        Follow-up Question {index + 1}.{idx + 1}:
                        "{nextQuestion.text}"
                      </Typography>
                    </Box>
                  ) : null;
                })}
              </YesContainer>

              {/* No option */}
              <Typography
                sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
                <Box component="span"
                     sx={{ display: 'inline-block', width: 20 }}>-</Box> No
              </Typography>

              {/* No info message and follow-up */}
              <NoContainer>
                {/* Handle both new and old data structure */}
                {question.noInfoMessage && (
                  <InfoMessage>
                    {question.noInfoMessage}
                  </InfoMessage>
                )}

                {question.options && question.options.length > 0 &&
                  question.options.find(opt => !opt.isYes)?.responseMessage && (
                    <InfoMessage>
                      {question.options.find(opt => !opt.isYes)?.responseMessage}
                    </InfoMessage>
                  )}

                {/* Render No follow-up questions */}
                {question.noFollowUps && question.noFollowUps.map((followUp: NestedQuestion, fIndex: number) =>
                  renderNestedQuestion(followUp, `${index + 1}b.${fIndex + 1}`, 1),
                )}

                {/* Render linked questions (old structure) */}
                {question.options && question.options.find(opt => !opt.isYes)?.nextQuestions?.map((nextQuestionId, idx) => {
                  const nextQuestion = questionnaire?.questions.find(q => q.id === nextQuestionId);
                  return nextQuestion ? (
                    <Box key={`no-next-${idx}`} sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <Box component="span"
                             sx={{ display: 'inline-block', width: 20 }}>-</Box>
                        Follow-up Question {index + 1}.{idx + 1}:
                        "{nextQuestion.text}"
                      </Typography>
                    </Box>
                  ) : null;
                })}
              </NoContainer>
            </Box>

            {index < (questionnaire?.questions?.length || 0) - 1 && (
              <Divider sx={{ mt: 4, mb: 4 }} />
            )}
          </Box>
        ))}
      </QuestionnaireContainer>
    </Box>
  );
};

export default ViewDansFundQuestionnaire;
